import { GaugeTile } from '../../charts/GaugeTile'
import { KpiTile } from '../../charts/KpiTile'
import { buildDrillRsql } from '../../drill'
import { formatMoney } from '../../format'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import type { AllocationUtilizationResult } from '../../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.allocationPoolUtilization

/**
 * IB-282 View 3, AC3.3 — "Cap / allocation utilization" gauge + headroom KPI. Wraps the existing,
 * real `allocation.api.AllocationReservationApi.getPoolStatus`, reusing `GaugeTile`/`KpiTile`
 * as-is (no new chart component for this AC, per the Tech Lead design §2).
 *
 * Three explicit states, never conflated (`AllocationUtilizationResult`): not yet selected (no
 * `programId`/`fiscalYearId` chosen), uncapped ("No cap for this program" — never a fabricated
 * 0%-utilized gauge implying phantom headroom), and the live cap/reserved/remaining figures.
 */
export function AllocationUtilizationWidget({ filter, skipRefetch, onDrill }: WidgetProps) {
  const { status, data, message, retry } = useWidgetData<AllocationUtilizationResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const notSelected = status === 'ok' && data?.selected === false
  const frameState = notSelected ? 'empty' : status
  const emptyMessage = 'Select a program and fiscal year to see its allocation-year cap utilization.'

  const currency = data?.currency ?? 'USD'
  const utilizationPct = data?.utilizationPct !== null && data?.utilizationPct !== undefined ? Number(data.utilizationPct) : 0

  return (
    <WidgetFrame
      title="Cap / allocation utilization"
      icon="landmark"
      state={frameState}
      message={frameState === 'empty' ? emptyMessage : message}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
    >
      {status === 'ok' && data?.selected && data.uncapped ? (
        <KpiTile
          title="Cap / allocation utilization"
          value="No cap"
          valueCaption="This program has no configured allocation-year cap."
          breakdown={[]}
        />
      ) : status === 'ok' && data?.selected ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--iw-space-3)' }}>
          <GaugeTile title="Utilization" percent={utilizationPct} caption={`${utilizationPct}% of cap reserved`} />
          <KpiTile
            title="Remaining headroom"
            value={data.remaining !== null ? formatMoney(data.remaining, currency) : '—'}
            valueCaption={`of ${data.cap !== null ? formatMoney(data.cap, currency) : '—'} cap`}
            breakdown={[
              { key: 'cap', label: 'Cap', value: data.cap !== null ? formatMoney(data.cap, currency) : '—' },
              { key: 'reserved', label: 'Reserved', value: data.reserved !== null ? formatMoney(data.reserved, currency) : '—' },
              { key: 'remaining', label: 'Remaining', value: data.remaining !== null ? formatMoney(data.remaining, currency) : '—' },
            ]}
          />
        </div>
      ) : null}
    </WidgetFrame>
  )
}
